// Normalise a (phone camera) image to an UPRIGHT JPEG, baking in its EXIF orientation.
//
// Re-encoding through a canvas fixes THREE camera-scan problems at once:
//   1. Orientation — iPhone photos store portrait shots as landscape pixels + an
//      EXIF "rotate" flag; pdf-lib ignores EXIF, so pages came out sideways.
//      createImageBitmap({ imageOrientation: 'from-image' }) bakes the rotation in.
//   2. Progressive JPEGs — iPhones often emit progressive JPEGs, which pdf-lib's
//      embedJpg CANNOT embed (it throws → the PDF build fails → the scanned doc
//      silently vanishes). canvas.toBlob always outputs a BASELINE JPEG.
//   3. Size — re-encoding at 0.85 and capping the long edge keeps the combined PDF
//      under Netlify's 6 MB function-body limit (otherwise the upload fails).
// Same approach CameraCapture.jsx already uses. Returns an upright baseline-JPEG
// ArrayBuffer, or null when unavailable (callers then embed the raw bytes).
// createImageBitmap and toBlob can HANG on a phone whose graphics stack is
// wedged — no resolve, no throw — and the caller then waits for ever with a
// spinner and no error. Every await here is raced against a deadline so a stuck
// device falls through to `null` (raw bytes) instead of stalling the upload.
const STEP_TIMEOUT_MS = 8000;
const withTimeout = (promise, ms = STEP_TIMEOUT_MS) => {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('image step timed out')), ms); }),
  ]).finally(() => clearTimeout(timer));
};

export async function uprightJpegBytes(file, maxEdge = 2400, quality = 0.85) {
  try {
    if (typeof createImageBitmap !== 'function') return null;
    const bmp = await withTimeout(createImageBitmap(file, { imageOrientation: 'from-image' }));
    const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
    if (bmp.close) bmp.close();
    const blob = await withTimeout(new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality)));
    return blob ? await withTimeout(blob.arrayBuffer()) : null;
  } catch (_) {
    return null;
  }
}
